import { useQuery } from "@tanstack/react-query";
import { api, query } from "./api";
import type {
  Alert,
  AlertDetail,
  AlertRule,
  CheckResult,
  Connection,
  Integration,
  Member,
  Monitor,
  NotificationChannel,
  NotificationPolicy,
  RoleOption,
  RunnerGroup,
  RunStats,
  Severity,
  Silence,
  StatusPage,
  StatusPageSummary,
  StatusRange,
  Team,
  WebhookTrigger,
} from "./types";

export type AlertQuery = { state?: "open" | "resolved" | "all"; severity?: Severity; monitorId?: string; limit?: number };

export const useAlerts = (params: AlertQuery, enabled = true) =>
  useQuery({ queryKey: ["alerts", params], queryFn: () => api.get<Alert[]>(`/alerts${query(params)}`), enabled, refetchInterval: 10_000 });

export const useAlert = (id: string) =>
  useQuery({ queryKey: ["alert", id], queryFn: () => api.get<AlertDetail>(`/alerts/${id}`), refetchInterval: 10_000 });

export const useMonitors = (enabled = true) =>
  useQuery({ queryKey: ["monitors"], queryFn: () => api.get<Monitor[]>("/monitors"), enabled, refetchInterval: 15_000 });

export const useMonitor = (id: string, enabled = true) =>
  useQuery({ queryKey: ["monitor", id], queryFn: () => api.get<Monitor>(`/monitors/${id}`), refetchInterval: 10_000, enabled: enabled && !!id });

export const useResults = (monitorId: string, limit = 60, enabled = true) =>
  useQuery({
    queryKey: ["results", monitorId, limit],
    queryFn: () => api.get<CheckResult[]>(`/monitors/${monitorId}/results${query({ limit })}`),
    refetchInterval: 10_000,
    enabled: enabled && !!monitorId,
  });

export const useTriggers = (monitorId: string, enabled = true) =>
  useQuery({ queryKey: ["triggers", monitorId], queryFn: () => api.get<WebhookTrigger[]>(`/monitors/${monitorId}/triggers`), enabled });

export const useRules = (monitorId: string, enabled = true) =>
  useQuery({ queryKey: ["rules", monitorId], queryFn: () => api.get<AlertRule[]>(`/monitors/${monitorId}/rules`), enabled, refetchInterval: 15_000 });

export const useRunnerGroups = (enabled = true, refetchInterval = 10_000) =>
  useQuery({ queryKey: ["runner-groups"], queryFn: () => api.get<RunnerGroup[]>("/runner-groups"), enabled, refetchInterval });

export const useIntegrations = (enabled = true) =>
  useQuery({ queryKey: ["integrations"], queryFn: () => api.get<Integration[]>("/integrations"), enabled, staleTime: Infinity });

export const useConnections = (enabled = true) =>
  useQuery({ queryKey: ["connections"], queryFn: () => api.get<Connection[]>("/connections"), enabled });

export const useChannels = (enabled = true) =>
  useQuery({ queryKey: ["channels"], queryFn: () => api.get<NotificationChannel[]>("/notification-channels"), enabled });

export const usePolicy = (enabled = true) =>
  useQuery({ queryKey: ["policy"], queryFn: () => api.get<NotificationPolicy>("/notification-policy"), enabled });

export const useSilences = (state: "current" | "active" | "upcoming" | "expired" | "all", enabled = true) =>
  useQuery({ queryKey: ["silences", state], queryFn: () => api.get<Silence[]>(`/silences${query({ state })}`), enabled, refetchInterval: 30_000 });

export const useMembers = (enabled = true) => useQuery({ queryKey: ["members"], queryFn: () => api.get<Member[]>("/members"), enabled });

export const useTeams = (enabled = true) => useQuery({ queryKey: ["teams"], queryFn: () => api.get<Team[]>("/teams"), enabled });

export const useRoles = (enabled = true) => useQuery({ queryKey: ["roles"], queryFn: () => api.get<RoleOption[]>("/roles"), enabled, staleTime: 60_000 });

const viewerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const useRunStats = (enabled = true) =>
  useQuery({ queryKey: ["run-stats"], queryFn: () => api.get<RunStats>(`/run-stats${query({ tz: viewerTimeZone() })}`), enabled, refetchInterval: 60_000 });

export const useStatusPages = (enabled = true) =>
  useQuery({ queryKey: ["status-pages"], queryFn: () => api.get<StatusPageSummary[]>("/status-pages"), enabled, refetchInterval: 60_000 });

export const useStatusPage = (id: string, range: StatusRange, enabled = true) =>
  useQuery({
    queryKey: ["status-pages", id, range],
    queryFn: () => api.get<StatusPage>(`/status-pages/${id}${query({ range, tz: viewerTimeZone() })}`),
    enabled,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
